import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, Badge, Button, Spinner, Collapse, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronRight,
    faChevronDown,
    faSearch,
    faLink,
    faCheck,
    faExternalLinkAlt,
    faExpandAlt,
    faCompressAlt,
    faTimes,
    faBarcode
} from '@fortawesome/free-solid-svg-icons';
import { faAmazon as faAmazonBrand } from '@fortawesome/free-brands-svg-icons';
import { toast } from 'react-toastify';

import { db } from 'config/firebase.js';
import { collection, query, orderBy, onSnapshot, limit } from 'firebase/firestore';
import missingImage from 'assets/merlin/missing_image.png';

const CLOUD_FUNCTIONS_URL =
    process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';
const VERIFICATIONS_PATH = 'productCatalog/topicz/asinVerifications';

/**
 * CatalogMatchGrid - Split-pane Amazon matching interface
 * 
 * Supports "Product Family" grouping and Variant-level Amazon scanning.
 */
const CatalogMatchGrid = () => {
    // Data state
    const [families, setFamilies] = useState([]); // Grouped products
    const [verifications, setVerifications] = useState({}); // Keyed by SKU

    // UI Loading states
    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [scanLoading, setScanLoading] = useState({}); // { [sku]: boolean }
    const [mapLoading, setMapLoading] = useState(null); // sku currently mapping

    // Search & Pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 50;

    // UI Toggles
    const [expandedFamilies, setExpandedFamilies] = useState(new Set());
    const [allExpanded, setAllExpanded] = useState(false); // Start collapsed for cleaner initial view

    // Mapping wizard state
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardVariant, setWizardVariant] = useState(null);
    const [wizardCandidates, setWizardCandidates] = useState([]);

    const searchTimeoutRef = useRef(null);

    // Fetch products from BigQuery API
    const fetchProducts = useCallback(async (query = '', offset = 0) => {
        setSearchLoading(true);
        try {
            const params = new URLSearchParams({
                q: query,
                searchMode: query ? 'keywords' : 'substring',
                limit: PAGE_SIZE.toString(),
                offset: offset.toString()
            });

            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/productSearch?${params}`);
            if (!response.ok) throw new Error('Search failed');

            const data = await response.json();

            if (data.success) {
                // Backend returns families. We inject vendor into variants for easier access during Scan.
                const processedFamilies = (data.products || []).map(f => ({
                    ...f,
                    variants: (f.variants || []).map(v => ({
                        ...v,
                        vendor: f.vendor
                    }))
                }));
                setFamilies(processedFamilies);
                setTotalCount(data.total || 0);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            toast.error('Error searching catalog');
        } finally {
            setSearchLoading(false);
            setLoading(false);
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchProducts('', 0);
    }, [fetchProducts]);

    // Debounced search
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setPage(0);
            fetchProducts(searchTerm, 0);
        }, 300);
        return () => clearTimeout(searchTimeoutRef.current);
    }, [searchTerm, fetchProducts]);

    // Pagination change
    useEffect(() => {
        if (page > 0) fetchProducts(searchTerm, page * PAGE_SIZE);
    }, [page, fetchProducts, searchTerm]);

    // Firestore listener for Verifications (AsinDiscovery results)
    useEffect(() => {
        const verificationsQuery = query(
            collection(db, VERIFICATIONS_PATH),
            orderBy('discoveredAt', 'desc'),
            limit(500) // Keep recent scans visible
        );

        const unsubscribe = onSnapshot(verificationsQuery, (snapshot) => {
            const data = {};
            snapshot.docs.forEach(doc => {
                const v = { id: doc.id, ...doc.data() };
                data[v.sku] = v;
            });
            setVerifications(prev => ({ ...prev, ...data }));
        });

        return () => unsubscribe();
    }, []);

    // Toggles
    const toggleFamily = (id) => {
        // When toggling a specific family, we exit "all expanded" mode
        setAllExpanded(false);
        setExpandedFamilies(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (allExpanded) {
            setExpandedFamilies(new Set());
        } else {
            setExpandedFamilies(new Set(families.map(f => f.group_id)));
        }
        setAllExpanded(!allExpanded);
    };

    // Helper: Extract image URL from candidate (handles both object and string formats)
    const getAmazonImageUrl = (candidate) => {
        if (!candidate?.images || candidate.images.length === 0) return null;

        // Check if primaryImage is set
        if (candidate.primaryImage) return candidate.primaryImage;

        // Images can be either objects {url, variant} or plain strings
        const first = candidate.images[0];
        if (typeof first === 'string') return first;
        if (typeof first === 'object' && first.url) return first.url;
        if (typeof first === 'object' && first.link) return first.link;

        return null;
    };

    // actions
    const handleScan = async (variant) => {
        setScanLoading(prev => ({ ...prev, [variant.sku]: true }));
        try {
            // Call AsinDiscovery with explicit product data overrides
            const payload = {
                items: [{
                    sku: variant.sku,
                    title: variant.title,
                    vendor: variant.vendor || '', // Pass vendor if available on variant or family
                    barcodes: variant.barcodes
                }],
                options: { force: true } // Force re-scan to get fresh results
            };

            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinDiscover`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error("Scan request failed");

            // The Cloud Function updates Firestore, which triggers the onSnapshot listener.
            // visual feedback
            toast.info(`Scanning Amazon for ${variant.sku}...`);

        } catch (error) {
            console.error("Scan error:", error);
            toast.error(`Scan failed: ${error.message}`);
        } finally {
            setScanLoading(prev => ({ ...prev, [variant.sku]: false }));
        }
    };

    const handleMap = async (sku, asin, candidate) => {
        setMapLoading(sku);
        try {
            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinVerify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sku,
                    action: 'approve',
                    asin,
                    importPreferences: { images: true, brand: true, packageQuantity: true }
                })
            });

            if (!response.ok) throw new Error("Mapping failed");
            toast.success(`Mapped ${sku} to ${asin}`);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setMapLoading(null);
        }
    };

    const clearSearch = () => {
        setSearchTerm('');
        setPage(0);
    };

    // Render Helpers
    const getAmazonStatusIcon = (verification) => {
        if (!verification) return <FontAwesomeIcon icon={faAmazonBrand} className="text-muted opacity-25" />;
        if (verification.verificationStatus === 'verified') return <FontAwesomeIcon icon={faAmazonBrand} className="text-warning" />;
        if (verification.candidates?.length > 0) return <FontAwesomeIcon icon={faAmazonBrand} className="text-info" />;
        return <FontAwesomeIcon icon={faAmazonBrand} className="text-muted opacity-25" />;
    };

    // Helper: Inline Match Display
    const renderMatchInline = (variant, verification) => {
        if (!verification) {
            return <div className="text-muted small fst-italic">Not scanned yet</div>;
        }

        if (verification.verificationStatus === 'verified') {
            return (
                <div className="d-flex align-items-center">
                    <Badge bg="success" className="me-2 px-2 py-1"><FontAwesomeIcon icon={faCheck} className="me-1" /> LINKED</Badge>
                    <a href={`https://amazon.com/dp/${verification.approvedAsin}`} target="_blank" rel="noopener noreferrer" className="text-dark fw-bold text-decoration-none">
                        {verification.approvedAsin}
                        <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" className="ms-1 text-muted opacity-50" />
                    </a>
                </div>
            );
        }

        const candidates = verification.candidates || [];
        if (candidates.length === 0) {
            return <div className="text-danger small fw-bold"><FontAwesomeIcon icon={faTimes} className="me-1" /> No matches found</div>;
        }

        // Show Top Candidate
        const topCandidate = candidates[0];
        const topImageUrl = getAmazonImageUrl(topCandidate);
        return (
            <div className="d-flex align-items-center">
                <div className="position-relative me-2">
                    <img
                        src={topImageUrl || missingImage}
                        style={{ width: 30, height: 30, objectFit: 'contain' }}
                        className="rounded border bg-white"
                        alt=""
                        onError={e => e.target.src = missingImage}
                    />
                    <Badge bg="info" className="position-absolute top-0 start-100 translate-middle badge-pill" style={{ fontSize: '0.5rem' }}>{candidates.length}</Badge>
                </div>
                <div style={{ maxWidth: '200px' }}>
                    <div className="fw-bold text-dark text-truncate small" title={topCandidate.title}>{topCandidate.title}</div>
                    <div className="d-flex align-items-center" style={{ fontSize: '0.7rem' }}>
                        <span className="me-2 text-dark font-monospace">{topCandidate.asin}</span>
                        <span className={`text-${topCandidate.confidence?.overall > 0.8 ? 'success' : 'warning'} fw-bold`}>
                            {Math.round((topCandidate.confidence?.overall || 0) * 100)}% Match
                        </span>
                    </div>
                </div>
            </div>
        );
    };

    // Open mapping wizard with all candidates
    const openWizard = (variant, verification) => {
        setWizardVariant(variant);
        setWizardCandidates(verification?.candidates || []);
        setWizardOpen(true);
    };

    // Helper: Action Buttons
    const renderActions = (variant, verification) => {
        const isLoading = scanLoading[variant.sku] || mapLoading === variant.sku;
        const hasCandidates = verification?.candidates?.length > 0;
        const isVerified = verification?.verificationStatus === 'verified';

        if (isLoading) return null; // Logic handled in parent, showing spinner instead

        if (isVerified) {
            return (
                <Button variant="outline-secondary" size="sm" onClick={() => handleScan(variant)} className="py-0 px-2" style={{ fontSize: '0.75rem' }} title="Re-scan">
                    <FontAwesomeIcon icon={faSearch} />
                </Button>
            );
        }

        if (hasCandidates) {
            const topCandidate = verification.candidates[0];
            return (
                <div className="d-flex justify-content-end gap-1">
                    <Button
                        variant="success"
                        size="sm"
                        className="py-1 px-3 fw-bold"
                        onClick={() => handleMap(variant.sku, topCandidate.asin, topCandidate)}
                        disabled={mapLoading === variant.sku}
                        style={{ fontSize: '0.75rem' }}
                    >
                        <FontAwesomeIcon icon={faLink} className="me-1" /> Link
                    </Button>
                    {/* Browse all candidates */}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => openWizard(variant, verification)}
                        className="py-1 px-2"
                        title={`View all ${verification.candidates.length} candidates`}
                    >
                        <FontAwesomeIcon icon={faSearch} />
                    </Button>
                </div>
            );
        }

        // No verification or No matches
        return (
            <Button variant="outline-primary" size="sm" onClick={() => handleScan(variant)} className="py-1 px-3 fw-bold" style={{ fontSize: '0.75rem' }}>
                <FontAwesomeIcon icon={faSearch} className="me-1" /> Scan
            </Button>
        );
    };


    if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>;

    return (
        <div className="catalog-match-grid p-3">
            {/* Controls */}
            <Card className="mb-3 shadow-sm border-0">
                <Card.Body className="py-2">
                    <Row className="align-items-center">
                        <Col md={6}>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    {searchLoading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faSearch} className="text-muted" />}
                                </span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm border-start-0"
                                    placeholder="Search products..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <button className="btn btn-outline-secondary btn-sm border-start-0" onClick={clearSearch}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                )}
                            </div>
                        </Col>
                        <Col md={6} className="text-end">
                            <Button variant="ghost" size="sm" onClick={toggleAll} className="me-2 text-muted">
                                <FontAwesomeIcon icon={allExpanded ? faCompressAlt : faExpandAlt} className="me-1" />
                                {allExpanded ? 'Collapse' : 'Expand'}
                            </Button>
                            <Badge bg="light" text="dark" className="border" style={{ fontSize: '0.75rem' }}>
                                {totalCount.toLocaleString()} Families
                            </Badge>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Grid Header */}
            <div className="bg-light border rounded-top fw-bold text-secondary py-2 px-3 d-flex" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <div style={{ flex: '0 0 50%' }}>Product Detail</div>
                <div style={{ flex: '0 0 35%' }}>Amazon Status</div>
                <div style={{ flex: '0 0 15%' }} className="text-end">Actions</div>
            </div>

            {/* Families */}
            <div className="bg-white border border-top-0 rounded-bottom" style={{ minHeight: 200 }}>
                {families.length === 0 ? (
                    <div className="text-center py-5 text-muted">No products found</div>
                ) : (
                    families.map(family => {
                        const isExpanded = expandedFamilies.has(family.group_id) || allExpanded;

                        return (
                            <div key={family.group_id} className="border-bottom group-row">
                                {/* Family Header - Compact */}
                                <div
                                    className="d-flex align-items-center py-2 px-3 bg-white cursor-pointer hover-bg-light"
                                    onClick={() => toggleFamily(family.group_id)}
                                    style={{ minHeight: 44 }}
                                >
                                    <div style={{ width: 20 }} className="text-center text-muted me-2">
                                        <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} size="sm" />
                                    </div>
                                    <div className="flex-grow-1 d-flex align-items-center">
                                        <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '0.85rem' }}>
                                            {family.title}
                                        </div>
                                        <Badge bg="secondary" className="ms-2" style={{ fontSize: '0.65rem' }}>{family.variants.length} SKU{family.variants.length !== 1 && 's'}</Badge>
                                        <span className="text-muted ms-2 small">{family.vendor}</span>
                                    </div>
                                </div>

                                {/* Variants List (Nested) */}
                                <Collapse in={isExpanded}>
                                    <div>
                                        {family.variants.map((variant, idx) => {
                                            const verification = verifications[variant.sku];
                                            const status = verification?.verificationStatus || 'none';
                                            const isVerified = status === 'verified';
                                            const hasCandidates = verification?.candidates?.length > 0;

                                            // Determine Row Color / Status Border
                                            let borderClass = 'border-start border-4 border-light'; // Default Gray
                                            if (isVerified) borderClass = 'border-start border-4 border-success';
                                            if (hasCandidates && !isVerified) borderClass = 'border-start border-4 border-warning';

                                            return (
                                                <div key={variant.sku} className={`d-flex align-items-center py-2 px-3 bg-light bg-opacity-10 ${borderClass} ps-5`} style={{ minHeight: 60, borderBottom: '1px solid #eee' }}>

                                                    {/* 1. Product Detail */}
                                                    <div style={{ flex: '0 0 50%' }} className="d-flex align-items-center pe-3">
                                                        <img
                                                            src={variant.thumbnail || missingImage}
                                                            style={{ width: 36, height: 36, objectFit: 'contain' }}
                                                            className="rounded border me-3 bg-white"
                                                            onError={e => e.target.src = missingImage}
                                                            alt=""
                                                        />
                                                        <div style={{ minWidth: 0 }}>
                                                            <div className="fw-medium text-dark text-truncate" style={{ fontSize: '0.9rem' }}>{variant.title}</div>
                                                            <div className="d-flex align-items-center text-muted small">
                                                                <code className="me-2 text-dark">{variant.sku}</code>
                                                                {variant.barcodes && (
                                                                    <div className="d-flex flex-wrap gap-1 mt-1">
                                                                        {variant.barcodes.unit && (
                                                                            <Badge bg="success" className="fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                                <FontAwesomeIcon icon={faBarcode} className="me-1" />
                                                                                {variant.barcodes.unit}
                                                                            </Badge>
                                                                        )}
                                                                        {variant.barcodes.stock && (
                                                                            <Badge bg="info" className="fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                                <FontAwesomeIcon icon={faBarcode} className="me-1" />
                                                                                {variant.barcodes.stock}
                                                                            </Badge>
                                                                        )}
                                                                        {variant.barcodes.other && variant.barcodes.other !== variant.barcodes.unit && (
                                                                            <Badge bg="secondary" className="fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                                <FontAwesomeIcon icon={faBarcode} className="me-1" />
                                                                                {variant.barcodes.other}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* 2. Amazon Status (Inline) */}
                                                    <div style={{ flex: '0 0 35%' }} className="px-2">
                                                        {scanLoading[variant.sku] ? (
                                                            <div className="text-muted small"><Spinner animation="border" size="sm" className="me-2" />Scanning...</div>
                                                        ) : (
                                                            renderMatchInline(variant, verification)
                                                        )}
                                                    </div>

                                                    {/* 3. Actions */}
                                                    <div style={{ flex: '0 0 15%' }} className="text-end">
                                                        {renderActions(variant, verification)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Collapse>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pagination */}
            {totalCount > PAGE_SIZE && (
                <div className="d-flex justify-content-center py-3">
                    <Button
                        variant="outline-secondary" size="sm" className="me-2"
                        disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                        Previous
                    </Button>
                    <Button
                        variant="outline-secondary" size="sm"
                        disabled={(page + 1) * PAGE_SIZE >= totalCount} onClick={() => setPage(p => p + 1)}>
                        Next
                    </Button>
                </div>
            )}

            <style>{`
                .cursor-pointer { cursor: pointer; }
                .hover-bg-light:hover { background-color: #f8f9fa !important; }
                .candidate-card { transition: all 0.15s; border: 2px solid transparent; }
                .candidate-card:hover { border-color: #0d6efd; background: #f8f9ff; }
                .candidate-card.selected { border-color: #198754; background: #e8f5e9; }
            `}</style>

            {/* ASIN Mapping Wizard Modal - Split Panel */}
            <Modal show={wizardOpen} onHide={() => setWizardOpen(false)} size="xl" fullscreen="lg-down" centered>
                <Modal.Header closeButton className="bg-dark text-white py-2">
                    <Modal.Title className="fs-5">
                        <FontAwesomeIcon icon={faAmazonBrand} className="text-warning me-2" />
                        Map Amazon ASINs to Product
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" style={{ height: '70vh', overflow: 'hidden' }}>
                    {wizardVariant && (
                        <div className="d-flex h-100">
                            {/* LEFT PANEL: Your Product & Barcodes */}
                            <div className="border-end bg-light" style={{ width: '40%', overflowY: 'auto' }}>
                                <div className="p-3 border-bottom bg-white">
                                    <div className="d-flex align-items-center mb-2">
                                        <img
                                            src={wizardVariant.thumbnail || missingImage}
                                            style={{ width: 60, height: 60, objectFit: 'contain' }}
                                            className="rounded border me-3 bg-white"
                                            alt=""
                                            onError={e => e.target.src = missingImage}
                                        />
                                        <div>
                                            <div className="fw-bold">{wizardVariant.title}</div>
                                            <code className="text-primary">{wizardVariant.sku}</code>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3">
                                    <h6 className="text-muted fw-bold mb-3">
                                        <FontAwesomeIcon icon={faBarcode} className="me-2" />
                                        BARCODES
                                    </h6>

                                    {/* Unit Barcode */}
                                    {wizardVariant.barcodes?.unit && (
                                        <div className="p-3 mb-2 border rounded bg-white">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <Badge bg="success" className="me-2">UNIT</Badge>
                                                    <span className="small text-muted">Sold individually</span>
                                                </div>
                                            </div>
                                            <div className="font-monospace mt-2 fs-5">{wizardVariant.barcodes.unit}</div>
                                        </div>
                                    )}

                                    {/* Stock/Case Barcode */}
                                    {wizardVariant.barcodes?.stock && (
                                        <div className="p-3 mb-2 border rounded bg-white">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <Badge bg="info" className="me-2">STOCK/CASE</Badge>
                                                    <span className="small text-muted">Multi-pack</span>
                                                </div>
                                            </div>
                                            <div className="font-monospace mt-2 fs-5">{wizardVariant.barcodes.stock}</div>
                                        </div>
                                    )}

                                    {/* Other Barcode */}
                                    {wizardVariant.barcodes?.other && (
                                        <div className="p-3 mb-2 border rounded bg-white">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <Badge bg="secondary" className="me-2">OTHER</Badge>
                                                    <span className="small text-muted">Alternative</span>
                                                </div>
                                            </div>
                                            <div className="font-monospace mt-2 fs-5">{wizardVariant.barcodes.other}</div>
                                        </div>
                                    )}

                                    {!wizardVariant.barcodes?.unit && !wizardVariant.barcodes?.stock && !wizardVariant.barcodes?.other && (
                                        <div className="text-muted text-center py-4">No barcodes available</div>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT PANEL: Amazon Candidates */}
                            <div style={{ width: '60%', overflowY: 'auto' }} className="bg-white">
                                <div className="p-3 border-bottom bg-light sticky-top">
                                    <h6 className="mb-0 text-dark fw-bold">
                                        <FontAwesomeIcon icon={faAmazonBrand} className="text-warning me-2" />
                                        AMAZON MATCHES ({wizardCandidates.length})
                                    </h6>
                                </div>

                                <div className="p-3">
                                    {wizardCandidates.length === 0 ? (
                                        <div className="text-center py-5 text-muted">
                                            <FontAwesomeIcon icon={faSearch} size="2x" className="mb-3 opacity-50" />
                                            <div>No Amazon matches found</div>
                                            <div className="small">Click "Scan" to search Amazon for this product</div>
                                        </div>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {wizardCandidates.map((candidate, idx) => (
                                                <div
                                                    key={candidate.asin}
                                                    className="candidate-card p-3 border rounded d-flex gap-3"
                                                >
                                                    <img
                                                        src={getAmazonImageUrl(candidate) || missingImage}
                                                        style={{ width: 80, height: 80, objectFit: 'contain' }}
                                                        className="rounded border bg-white flex-shrink-0"
                                                        alt=""
                                                        onError={e => e.target.src = missingImage}
                                                    />
                                                    <div className="flex-grow-1 min-width-0">
                                                        <div className="fw-bold text-truncate mb-1" title={candidate.title}>
                                                            {candidate.title}
                                                        </div>
                                                        <div className="d-flex flex-wrap gap-1 mb-2">
                                                            <Badge bg="dark" className="font-monospace">{candidate.asin}</Badge>
                                                            <Badge bg={candidate.confidence?.overall > 0.8 ? 'success' : candidate.confidence?.overall > 0.6 ? 'warning' : 'danger'}>
                                                                {Math.round((candidate.confidence?.overall || 0) * 100)}%
                                                            </Badge>
                                                            {candidate.packageQuantity && (
                                                                <Badge bg="info">Pack: {candidate.packageQuantity}</Badge>
                                                            )}
                                                            {candidate.brand && (
                                                                <Badge bg="secondary">{candidate.brand}</Badge>
                                                            )}
                                                        </div>
                                                        {candidate.matchedOn && (
                                                            <div className="small text-muted mb-2">
                                                                Matched by: <span className="text-dark">{candidate.matchedOn}</span>
                                                            </div>
                                                        )}
                                                        <div className="d-flex gap-2 flex-wrap">
                                                            <Button
                                                                variant="success"
                                                                size="sm"
                                                                onClick={() => {
                                                                    handleMap(wizardVariant.sku, candidate.asin, candidate);
                                                                    setWizardOpen(false);
                                                                }}
                                                                disabled={mapLoading === wizardVariant?.sku}
                                                            >
                                                                <FontAwesomeIcon icon={faLink} className="me-1" />
                                                                Link to Product
                                                            </Button>
                                                            <a
                                                                href={`https://amazon.com/dp/${candidate.asin}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline-secondary btn-sm"
                                                            >
                                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" />
                                                                View
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="py-2 bg-light">
                    <Button variant="outline-primary" size="sm" onClick={() => wizardVariant && handleScan(wizardVariant)}>
                        <FontAwesomeIcon icon={faSearch} className="me-1" /> Re-scan Amazon
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => setWizardOpen(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CatalogMatchGrid;
